import { FilterList } from '@mui/icons-material';
import { Box, Button, Divider, Stack } from '@mui/material';
import { FilterButton } from '@side/pantry';
import { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { type ReactNode, useMemo, useState } from 'react';
import { useAppFlags } from '$launch-darkly/app-flags';
import { CompensationReportFilters } from '../CompensationReportFilters/CompensationReportFilters';
import {
  CompensationReportFiltersProvider,
  compensationReportFiltersDefaultValues,
  useCompensationReportFilters,
} from '../CompensationReportFilters/CompensationReportFilters.helpers';
import { GlossaryButton } from '../components/GlossaryButton/GlossaryButton';
import { DealAllocationsTable } from '../DealAllocationsTable/DealAllocationsTable';
import { useViewReportAs } from '../helpers';
import { useReportHeaderActionLayout } from '../hooks/useReportHeaderActionLayout';
import { MonthlyEarningsTimeline } from '../MonthlyEarningsTimeline/MonthlyEarningsTimeline';
import { PayoutBreakdownTable } from '../PayoutBreakdown/PayoutBreakdown';
import { PayoutSummary } from '../PayoutSummary/PayoutSummary';
import { AIInsightsButton } from '../PerformanceReport/components/AIInsightsButton';
import { ReportContainer } from '../ReportContainer/ReportContainer';
import { useReportsRole } from '../ReportsConfiguration/useReportsRole';
import { TransactionTypeBreakdown } from '../TransactionTypeBreakdown/TransactionTypeBreakdown';

function ExtraContent({
  onFilterClick,
  button,
  size,
  id,
  fullWidth = false,
  fillAvailableSpace = false,
}: {
  onFilterClick: () => void;
  button: ReactNode;
  size: 'small' | 'medium';
  id?: string;
  fullWidth?: boolean;
  fillAvailableSpace?: boolean;
}) {
  const { filterCount } = useCompensationReportFilters();

  return (
    <>
      {button}
      <FilterButton
        id={id}
        fullWidth={fullWidth}
        filterCount={filterCount}
        onClick={onFilterClick}
        size={size}
        startIcon={<FilterList fontSize="small" />}
        sx={{
          ...(fillAvailableSpace ? { flex: '1 1 auto', minWidth: 0 } : {}),
        }}
      >
        Filters
      </FilterButton>
    </>
  );
}

function MainExtraContent({
  onFilterClick,
  viewReportAs,
  showAIInsights,
  showOnlyIcon,
  size,
  fillAvailableSpace,
}: {
  onFilterClick: () => void;
  viewReportAs: string;
  showAIInsights: boolean;
  showOnlyIcon: boolean;
  size: 'small' | 'medium';
  fillAvailableSpace: boolean;
}) {
  return (
    <ExtraContent
      id="compensation-report-filters-button"
      size={size}
      fillAvailableSpace={fillAvailableSpace}
      onFilterClick={onFilterClick}
      button={
        <Stack
          direction="row"
          sx={{
            gap: { xs: 1, md: 1.25, lg: 2 },
            flexShrink: 0,
          }}
        >
          {showAIInsights ? (
            <AIInsightsButton
              viewReportAs={viewReportAs}
              showOnlyIcon={showOnlyIcon}
              size={size}
            />
          ) : null}
          <GlossaryButton showOnlyIcon={showOnlyIcon} size={size} />
        </Stack>
      }
    />
  );
}

function ScrollingHeaderExtraContent({
  onFilterClick,
}: {
  onFilterClick: () => void;
}) {
  const { filters, clearFilters } = useCompensationReportFilters();
  const filterCount = filters
    ? Object.values(filters).filter(
        (value) => value && (Array.isArray(value) ? value.length > 0 : true),
      ).length
    : 0;
  const isClearDisabled = useMemo(
    () => isEqual(filters, compensationReportFiltersDefaultValues),
    [filters],
  );

  return (
    <ExtraContent
      onFilterClick={onFilterClick}
      button={
        filterCount > 0 ? (
          <Button
            variant="link"
            size="small"
            onClick={clearFilters}
            disabled={isClearDisabled}
          >
            Clear All
          </Button>
        ) : null
      }
      size="small"
    />
  );
}

export function CompensationReport({
  previousViewReportAs,
}: {
  previousViewReportAs?: string;
}) {
  const router = useRouter();
  const { embedded } = router.query;
  const viewingAs = useViewReportAs();
  const isEmbedded = embedded === 'true';
  const [open, setOpen] = useState(false);
  const { isAgent } = useReportsRole();
  const { aiReportInsightButton, __loading__: flagsLoading } = useAppFlags();
  const { isCompactActions, isNarrowActions, isSmallScreen } =
    useReportHeaderActionLayout();
  const showAIInsightsButton = Boolean(!flagsLoading && aiReportInsightButton);
  const handleFilterClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  if (!viewingAs) return null;

  return (
    <CompensationReportFiltersProvider
      previousViewReportAs={previousViewReportAs}
    >
      <ReportContainer
        viewReportAs={viewingAs}
        title="Compensation"
        subtitle="Understand how commissions are calculated and view earnings over time."
        boldedSubtitle="Note that this report only includes Closed status."
        isEmbedded={isEmbedded}
        extra={
          <MainExtraContent
            onFilterClick={handleFilterClick}
            viewReportAs={viewingAs}
            showAIInsights={showAIInsightsButton}
            showOnlyIcon={isCompactActions}
            size="medium"
            fillAvailableSpace={isSmallScreen}
          />
        }
        forceNarrowActionLayout={isNarrowActions}
        scrollingHeaderExtra={
          <>
            <ScrollingHeaderExtraContent onFilterClick={handleFilterClick} />
            {showAIInsightsButton ? (
              <AIInsightsButton
                id="ai-insights-sticky-button"
                viewReportAs={viewingAs}
                showOnlyIcon={isCompactActions}
                size="small"
              />
            ) : null}
          </>
        }
      >
        <CompensationReportFilters
          open={open}
          onClose={handleClose}
          viewReportAs={viewingAs}
        />
        <Stack sx={{ gap: { xs: 6, md: 4.5 } }}>
          <Stack
            sx={{
              gap: { xs: 6, md: 4.5 },
              flexDirection: { xs: 'column', lg: 'row' },
            }}
          >
            <PayoutSummary viewReportAs={viewingAs} />
            <TransactionTypeBreakdown />
          </Stack>
          <MonthlyEarningsTimeline />
          <Divider />
          {!isAgent && <PayoutBreakdownTable />}
          <Box sx={{ mt: { xs: 0, md: 1.5 } }}>
            <DealAllocationsTable viewReportAs={viewingAs} />
          </Box>
        </Stack>
      </ReportContainer>
    </CompensationReportFiltersProvider>
  );
}
